import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Alert, Button, Dialog, DialogContent, Field, Input } from '@datanomik/core-ui';
import { Key2Line, MailLine } from '@datanomik/core-ui/icons';

import { usePasswordReset } from '../hooks/usePasswordReset';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface PasswordResetDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function PasswordResetDialog({ open, onOpenChange }: PasswordResetDialogProps) {
  const { t } = useTranslation();
  const { busy, error, sent, submit, reset } = usePasswordReset();
  const { register, handleSubmit, formState } = useForm<{ email: string }>({
    defaultValues: { email: '' },
  });

  const close = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent
        icon={<Key2Line />}
        title={t('Forgot your password?')}
        description={t('Enter your email and we will send you a link to choose a new one.')}
        footer={sent
          ? <Button className="w-full" onClick={() => close(false)}>{t('Close')}</Button>
          : (
            <Button type="submit" form="password-reset-form" className="w-full" loading={busy}>
              {t('Send me a reset link')}
            </Button>
          )}
      >
        {sent ? (
          <Alert tone="success" title={t('Reset link sent!')}>
            {t('Help is on the way, please check your email for further instructions.')}
          </Alert>
        ) : (
          <form
            id="password-reset-form"
            onSubmit={handleSubmit(({ email }) => submit(email))}
            noValidate
            className="flex flex-col gap-3"
          >
            {error && <Alert tone="destructive">{error}</Alert>}
            <Field label={t('Email')} required error={formState.errors.email?.message}>
              <Input
                type="email"
                autoComplete="email"
                placeholder={t('you@company.com')}
                startAdornment={<MailLine aria-hidden="true" />}
                {...register('email', {
                  required: t('Email is required'),
                  pattern: { value: EMAIL_PATTERN, message: t('Please enter a valid email address') },
                })}
              />
            </Field>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
